import React, { useMemo } from 'react'
import { Card, Dropdown, Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import { DownOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle } from '@coreui/icons'

import { Wallet } from './types'
import { path } from '../../../libs'
import { CryptoIcon } from '../../../pages/finSystem/wallets/walletsTable/CryptoIcon'
import styles from './styles.module.css'

type Props = {
  data: Wallet
}

export const WalletCard: React.FC<Props> = ({ data }) => {
  const {
    id,
    name,
    paymentSystem,
    localBalances,
    overallBalanceUsd,
    lossPercentage,
    reconciledAt,
    revisions,
  } = data

  const displayName = paymentSystem?.name ?? name ?? 'error'

  const currencies = useMemo(
    () => localBalances.map((item) => item.currency),
    [localBalances],
  )

  const isAllRevisionFresh = useMemo(() => {
    if (revisions.length === 0) {
      return false
    }

    return revisions.every(
      (item) => dayjs().diff(item.completedAt, 'hours') < 36,
    )
  }, [revisions])

  const visibleBalances = revisions.slice(0, 3)
  const hasOverflowBalances = revisions.length > 3

  const balancesDropdownItems = useMemo(
    () =>
      revisions.map((item) => ({
        key: `${item.currency}-${item.completedAt}`,
        label: (
          <div className={styles.balanceRow}>
            {!isAllRevisionFresh && (
              <span
                className={styles.balanceTime}
                style={{
                  color:
                    dayjs().diff(item.completedAt, 'hours') < 36
                      ? '#52c41a'
                      : '#8c8c8c',
                }}
              >
                {formatDistanceToNow(new Date(item.completedAt))}
              </span>
            )}

            <span className={styles.balanceValue}>{item.sum}</span>

            <span className={styles.balanceCurrency}>
              {item.currency}
            </span>
          </div>
        ),
      })),
    [revisions, isAllRevisionFresh],
  )

  return (
    <Card className={styles.card}>
      <Link
        to={path.finSystem.wallets.details(id)}
        className={styles.link}
      >
        <div className={styles.inner}>
          <div className={styles.top}>
            <div className={styles.header}>
              <div className={styles.nameBlock}>
                <span className={styles.icon}>
                  <CryptoIcon
                    name={name}
                    size={32}
                  />
                </span>

                <span className={styles.name}>
                  {displayName}
                </span>
              </div>

              <Tooltip title={currencies.join(' ')}>
                <span className={styles.currencies}>
                  {currencies.join(' ')}
                </span>
              </Tooltip>
            </div>

            <div className={styles.body}>
              <div className={styles.totalBlock}>
                <span className={styles.label}>
                  Total sum
                </span>

                <span className={styles.totalAmount}>
                  {Math.floor(
                    overallBalanceUsd ?? 0,
                  ).toLocaleString('ru')}

                  <span className={styles.totalUnit}>
                    {' '}
                    USD
                  </span>
                </span>
              </div>

              <div className={styles.balanceBlock}>
                {revisions.length === 0 ? (
                  <span className={styles.label}>
                    PS Revision not found
                  </span>
                ) : hasOverflowBalances ? (
                  <Dropdown
                    menu={{
                      items: balancesDropdownItems,
                    }}
                    placement="bottom"
                  >
                    <span className={styles.balanceTrigger}>
                      <span className={styles.label}>
                        Local balance
                      </span>

                      <DownOutlined
                        style={{
                          color: '#9DA5B1',
                        }}
                      />
                    </span>
                  </Dropdown>
                ) : (
                  <>
                    <span className={styles.label}>
                      Local balance
                    </span>

                    <div className={styles.balanceList}>
                      {visibleBalances.map((rev) => (
                        <div
                          key={`${rev.currency}-${rev.completedAt}`}
                          className={styles.balanceRow}
                        >
                          {!isAllRevisionFresh && (
                            <span
                              className={styles.balanceTime}
                              style={{
                                color:
                                  dayjs().diff(
                                    rev.completedAt,
                                    'hours',
                                  ) < 36
                                    ? '#52c41a'
                                    : '#8c8c8c',
                              }}
                            >
                              {formatDistanceToNow(
                                new Date(
                                  rev.completedAt,
                                ),
                              )}
                            </span>
                          )}

                          <span
                            className={
                              styles.balanceValue
                            }
                          >
                            {rev.sum}
                          </span>

                          <span
                            className={
                              styles.balanceCurrency
                            }
                          >
                            {rev.currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={styles.bottom}>
            <div className={styles.lossBlock}>
              <span className={styles.lossLabel}>
                Loss (30 last days)
              </span>

              <span className={styles.value}>
                {lossPercentage != null
                  ? `-${lossPercentage}%`
                  : '?'}
              </span>
            </div>

            {isAllRevisionFresh ? (
              <div className={styles.revisionBlock}>
                <CIcon
                  icon={cilCheckCircle}
                  className="text-success"
                  size="xxl"
                />
              </div>
            ) : (
              <div className={styles.revisionBlock}>
                <span className={styles.revisionLabel}>
                  Last revision
                </span>

                <span className={styles.value}>
                  {reconciledAt
                    ? dayjs(reconciledAt).format(
                      'DD.MM.YYYY',
                    )
                    : '?'}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </Card>
  )
}
